import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import { Run } from "../lib/supabase";
import { ENTITY_NAMES } from "../constants/entities";
import { useAuth } from "../contexts/AuthContext";
import LoadingScreen from "../components/ui/LoadingScreen";
import RunsSection from "../components/runs/RunsSection";

interface RunWithSeries extends Run {
  series: {
    id: string;
    code: string;
    title: string;
    total_labs?: number;
  };
  lab_dates?: {
    id: string;
    run_id: string;
    lab_number: number;
    date: string;
  }[];
}

const Runs: React.FC = () => {
  const [runs, setRuns] = useState<RunWithSeries[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSeriesForAdd, setSelectedSeriesForAdd] = useState<string>("");
  const [seriesOptions, setSeriesOptions] = useState<
    { id: string; title: string; code: string }[]
  >([]);
  const { userRole } = useAuth();
  const isAdmin = userRole === "admin";
  const navigate = useNavigate();

  const fetchRuns = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("runs")
        .select(
          `
          *,
          series!inner (
            id,
            code,
            title,
            total_labs
          ),
          lab_dates (
            id,
            run_id,
            lab_number,
            date
          )
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRuns(data || []);
    } catch (error) {
      console.error("Error fetching runs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSeriesOptions = async () => {
    try {
      const { data, error } = await supabase
        .from("series")
        .select("id, title, code")
        .order("code");

      if (error) throw error;
      setSeriesOptions(data || []);
    } catch (error) {
      console.error("Error fetching series options:", error);
    }
  };

  const handleAddRun = () => {
    if (selectedSeriesForAdd) {
      navigate(`/series/${selectedSeriesForAdd}/runs/new`);
    }
  };

  useEffect(() => {
    fetchRuns();
    fetchSeriesOptions();
  }, []);

  if (isLoading) return <LoadingScreen />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1>{ENTITY_NAMES.RUN}s</h1>
        {isAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary mt-4 sm:mt-0"
          >
            <Plus className="h-5 w-5 mr-2 flex-shrink-0" />
            Add {ENTITY_NAMES.RUN}
          </button>
        )}
      </div>

      {/* Series Selection Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="">Select Series</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-neutral-500 hover:text-neutral-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              Select the series you want to add a{" "}
              {ENTITY_NAMES.RUN.toLowerCase()} to:
            </p>
            <select
              className="input w-full mb-4"
              value={selectedSeriesForAdd}
              onChange={(e) => setSelectedSeriesForAdd(e.target.value)}
            >
              <option value="">Select a series...</option>
              {seriesOptions.map((series) => (
                <option key={series.id} value={series.id}>
                  {series.code} - {series.title}
                </option>
              ))}
            </select>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleAddRun}
                disabled={!selectedSeriesForAdd}
                className="btn btn-primary"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">
        <RunsSection
          seriesId=""
          runs={runs}
          isAdmin={isAdmin}
          onRunCreated={fetchRuns}
          type="current"
          title={`Current ${ENTITY_NAMES.RUN}s`}
        />

        <RunsSection
          seriesId=""
          runs={runs}
          isAdmin={isAdmin}
          onRunCreated={fetchRuns}
          type="past"
          title={`Past ${ENTITY_NAMES.RUN}s`}
        />
      </div>
    </div>
  );
};

export default Runs;
